import { useRef } from 'react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { InlineInput } from '@ztechapps/webui.input'
import { AddressSelectEditContent } from '../Address/AddressSelectEditContent'
import { validateAddress } from '../Address/validateAddress'
import { addressDisplayValue } from '../Address/addressDisplayValue'
import { withDefaultCountry } from './withDefaultCountry'
import type { AddressEditFieldProps } from './AddressEditField'

// Renders exactly the `InlineInput` tree the wrapper renders today, unchanged, forwarding the
// same props — including the `parentInputComponent` indirection (`OrderInlineInput` /
// `LogisticInlineInput` / `PurchaseOrderInlineInput`) that reads document context
// (`canEditBeforeTransit`, `canEditLogistic`, `hasRoleAndNotReceived`) and defaults `canEdit`
// from it. This arm never touches `AddressEditPopOverContext` or `useAddressEditPopOver` — those
// belong to the POPOVER arm only.
export const AddressInlineEditField = ({
  name,
  label,
  initialValues,
  displayAddress,
  mutationConfig,
  updateParams,
  addressSelectComponent,
  addressSelectProps,
  extraActions,
  buildFilter,
  parseCreateOption,
  emptyContent,
  canEdit,
  parentInputComponent,
}: AddressEditFieldProps) => {
  const ParentInput = parentInputComponent || InlineInput
  // Mirrors `InlineInput`'s own initial-value extraction exactly (index.tsx) so the unchanged-value
  // comparison below lines up with the same identity `InlineInput.update` compares against.
  const initialValue = get(initialValues, name) ?? ''

  // `InlineInput`'s own `saveNow` never rejects: `useMutation`'s Apollo mutation runs with the
  // default `errorPolicy` ('none'), so a GraphQL/network failure REJECTS the promise, which
  // `useInlineEditor`'s `_save` (libs/common/hook/src/useInlineEditor/index.ts) catches and
  // swallows without ever reaching `afterUpdate` — the dominant failure path. So this can only
  // ever be RAISED by evidence of a clean resolve (`afterUpdate` firing), never assumed true up
  // front; the one exception is `InlineInput.update`'s own unchanged-value short-circuit, which
  // also skips `afterUpdate`, and that no-op is itself a success — handled by the `saveNow` below.
  const succeededRef = useRef(false)

  return (
    <ParentInput
      {...{
        name,
        label,
        initialValues,
        mutationConfig,
        updateParams,
        canEdit,
        validate: validateAddress,
        displayContent: (value: any) => addressDisplayValue(displayAddress ?? value),
        emptyContent,
        afterUpdate: async ({ errors }: { errors: any }) => {
          succeededRef.current = isEmpty(errors)
        },
        editContent: ({ setNewValue, value, cancelNow, saveNow: coreSaveNow, submitting }: any) => (
          <AddressSelectEditContent
            value={withDefaultCountry(value)}
            setNewValue={setNewValue}
            saveNow={async () => {
              // Unchanged value: `InlineInput.update` short-circuits before ever calling
              // `afterUpdate`, so seed the answer with that same no-op-is-success case rather
              // than defaulting to `true` — a real edit starts `false` until proven otherwise.
              succeededRef.current = value === initialValue
              // `validate` throwing (client-side, e.g. `validateAddress`) never reaches
              // `afterUpdate` either — re-run it here so that failure is reported honestly too.
              // `validateAddress` itself is synchronous (throws immediately, never returns a
              // promise), so there is nothing to `await`.
              try {
                validateAddress(value)
              } catch {
                succeededRef.current = false
              }
              await coreSaveNow()
              return succeededRef.current
            }}
            cancelNow={cancelNow}
            submitting={submitting}
            addressSelectComponent={addressSelectComponent}
            addressSelectProps={addressSelectProps}
            buildFilter={buildFilter}
            extraActions={extraActions}
            parseCreateOption={parseCreateOption}
          />
        ),
      }}
    />
  )
}
